import { GraphModel } from './graph.model';
import { DG } from '@cotera/sdk/core';
import { z } from 'zod/v3';
import { toast } from '@cotera/client/app/components/ui/toaster';
import { err, ok } from 'neverthrow';
import type { TenantedClient } from '@cotera/client/app/etc/api/typed-client';
import {
  ActionResult,
  ApplicableContext,
  ExecuteContext,
  BaseAction,
} from '../actions/framework';
import { Assert } from '@cotera/utilities';
import { GRAPH_ACTION_GROUP } from '../dataset/actions/constants';

type Params = {
  message?: string;
};

export class PublishGraphAction extends BaseAction<
  Params,
  { t: string; message?: string }[]
> {
  title = 'Publish Graph';
  description = 'Publish the changes to the graph';
  inputSchema = z.object({
    message: z.string().optional(),
  });
  shortcut = ['ctrl', 'shift', 'p'];
  group = GRAPH_ACTION_GROUP;
  icon = 'paper-airplane';

  constructor(
    private readonly client: Pick<TenantedClient['commits'], 'commit' | 'list'>,
    private readonly userId: string
  ) {
    super();
  }

  applicable(context: ApplicableContext): boolean {
    return context.isInScope(GraphModel);
  }

  async execute(
    payload: Params,
    context: ExecuteContext
  ): Promise<ActionResult<{ t: string; message?: string }[]>> {
    const graph = context.getInScopeModelOfType(GraphModel);

    Assert.assert(graph !== null, 'Graph model not found');

    // Use the cached stagedOperations instead of async userBranches.getStagedOperations()
    // to avoid race conditions where operations are cleared in markAsPublished but
    // the async storage hasn't been cleared yet, causing duplicate commits
    const operations = graph.stagedOperations.snapshot();

    if (operations.length === 0) {
      return ok({});
    }

    if (!graph.publishable.snapshot()) {
      return err([
        {
          t: 'InvalidItems',
          message: 'Graph contains invalid items',
        },
      ]);
    }

    const toastStrategy = graph.autoPublish.snapshot()
      ? toast.noop
      : toast.loading;

    const publishToast = toastStrategy('Preparing to publish graph...');

    // Try to publish, with retry logic for IndexConflict
    const publishResult = await this.attemptPublish(
      graph,
      operations,
      payload.message ?? null,
      publishToast
    );

    if (publishResult.isOk()) {
      publishToast.success('Graph published successfully!');
      return ok({});
    }

    return publishResult;
  }

  private async attemptPublish(
    graph: GraphModel,
    operations: DG.OperationData[],
    message: string | null,
    publishToast: any,
    isRetry: boolean = false
  ): Promise<ActionResult<{ t: string; message?: string }[]>> {
    // Compilation is typically very fast, so no toast needed
    const compiler = graph.compiler.snapshot();
    const compilationResult = compiler.compile();

    const errors: { t: string; message: string }[] = compilationResult.match(
      (x) =>
        Object.entries(x.itemErrors()).map(([name, e]) => ({
          t: e.t as string,
          message: `${name}: ${JSON.stringify(e)}`,
        })),
      ({ err }) => [{ t: err.t, message: JSON.stringify(err) }]
    );

    if (errors.length > 0) {
      publishToast.error(
        `Compilation failed: ${errors.map((e) => e.message).join('\n')}`
      );
      return err(errors);
    }

    // Only show network request toast for retries (where it might actually take time)
    if (isRetry) {
      publishToast.success('Retrying publish...');
    }

    // Calculate optimistic index right before commit to ensure it's current
    const commit: DG.Commit = {
      actorId: this.userId,
      operations,
      message,
      timestamp: new Date(),
      optimisticIndex:
        Math.max(-1, ...graph.publishedCommits.snapshot().map((x) => x.index)) +
        1,
    };

    const result = await this.client.commit({
      commits: [commit],
    });

    if (result.isErr()) {
      // Assume error is always wrapped as { t: 'application' | 'server', err: ... }

      if (result.error.t === 'server') {
        // eslint-disable-next-line no-console
        console.error('Server returned an error:', result.error.err);
        publishToast.error(
          'Server returned an invalid error response. Please contact support.'
        );
        return err([
          {
            t: 'ServerError',
            message: 'Server returned an invalid error response.',
          },
        ]);
      }

      if (result.error.t !== 'client' && result.error.t !== 'application') {
        return err([
          {
            t: 'ServerError',
            message: 'Server returned an invalid error response.',
          },
        ]);
      }

      const errorType = result.error.err.errorType;

      if (errorType === 'IndexConflict') {
        if (isRetry) {
          return err([
            {
              t: 'IndexConflict',
              message:
                'Failed To Retry Publish: IndexConflict still exists despite rebasing and republishing.',
            },
          ]);
        }

        publishToast.success(
          'Detected concurrent changes. Refreshing and retrying...'
        );

        // Refresh the published commits cache
        const freshCommitsResult = await this.client.list({});
        if (freshCommitsResult.isOk()) {
          graph.refreshPublishedCommitsCache(
            freshCommitsResult.value.map((c) => ({
              ...c,
              timestamp:
                c.timestamp instanceof Date
                  ? c.timestamp
                  : new Date(c.timestamp as string),
            })) as DG.SavedCommit[]
          );

          // Retry the publish with updated cache and correct optimistic index
          return this.attemptPublish(
            graph,
            operations,
            message,
            publishToast,
            true
          );
        } else {
          publishToast.error(
            'Failed to refresh commits cache. Please try again.'
          );
          return err([
            {
              t: 'FailedToRefreshCache',
              message: 'Failed to refresh commits cache',
            },
          ]);
        }
      }

      // Handle other errors with more descriptive messages
      // eslint-disable-next-line no-console
      console.error('Publish failed with error:', result.error);

      const detail = this.describeError(result.error.err);
      const errorMessage = this.getErrorMessage(errorType, detail);
      publishToast.error(errorMessage);
      return err([
        {
          t: errorType,
          message: detail,
        },
      ]);
    }

    await graph.markAsPublished(
      result.value.commits.map((c) => ({
        ...c,
        timestamp:
          c.timestamp instanceof Date
            ? c.timestamp
            : new Date(c.timestamp as string),
      })) as DG.SavedCommit[]
    );
    return ok({});
  }

  // Builds a human-readable detail string from a server error, preferring the
  // structured per-item failures (e.g. BuildError's `errors`) over the generic
  // top-level message. Returns undefined when there's nothing useful to show.
  private describeError(err: {
    message?: string;
    errors?: { error: { t: string; message?: string } }[];
  }): string | undefined {
    if (err.errors !== undefined && err.errors.length > 0) {
      return err.errors.map((e) => e.error.message ?? e.error.t).join('\n');
    }
    return err.message;
  }

  private getErrorMessage(errorType: string, detail?: string): string {
    switch (errorType) {
      case 'UnableToAcquireLock':
        return 'Unable to acquire lock. Another user may be publishing. Please try again.';
      case 'ValidationError':
        return 'Validation error occurred. Please check your changes and try again.';
      case 'Unauthorized':
        return 'You are not authorized to publish changes.';
      case 'NetworkError':
        return 'Network error occurred. Please check your connection and try again.';
      case 'ServerError':
        return 'Server error occurred. Please try again later.';
      case 'ParseError':
        return 'Server returned a parse error. This might be due to invalid data format.';
      case 'BuildError':
        return detail !== undefined
          ? `Your changes reference items that are invalid or no longer exist:\n${detail}`
          : 'Your changes reference items that no longer exist. Please review your staged changes and try again.';
      default:
        return detail !== undefined
          ? `Publish failed: ${errorType}\n${detail}`
          : `Publish failed: ${errorType}`;
    }
  }
}
